import React, { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ChevronDown, ChevronRight } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { LoanTable } from "./LoanTable";
import { ConfirmDialog } from "./ConfirmDialog";

const KIND_LABEL = { new_multi: "New multi-loan group", join_existing: "Join existing group", new_single: "Own group (single loan)" };

const GroupCard = ({ s }) => (
  <div className="border border-[#D1FAE5] bg-[#F0FDF4]/40 rounded-xl p-4" data-testid={`safe-group-${s.proposal_id}`}>
    <div className="flex flex-wrap items-center gap-2 mb-2 text-xs">
      <span className="font-semibold px-2 py-0.5 rounded bg-[#D1FAE5] text-[#065F46]">{KIND_LABEL[s.kind]}</span>
      <span className="font-mono text-[#374151]">{s.target_group_id || `${s.proposal_id} (id minted on apply)`}</span>
      <span className="text-[#6B7280]">· {s.loan_ids.length} loan(s) · {s.basis}</span>
    </div>
    <LoanTable rows={s.loans} testid={`safe-group-table-${s.proposal_id}`} />
  </div>
);

export const SafeAssignmentsPanel = ({ plan, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [showSingles, setShowSingles] = useState(false);
  const safe = plan.safe_assignments;
  const grouped = safe.filter((s) => s.kind !== "new_single");
  const singles = safe.filter((s) => s.kind === "new_single");
  const t = plan.totals;

  const apply = async () => {
    try {
      const { data } = await api.post("/operations/payoffs/client-groups/backfill/apply-safe", { plan_hash: plan.plan_hash });
      toast.success(`Safe assignments applied: ${data.loans_written} loans (batch ${data.batch_id})`);
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); throw e; }
  };

  return (
    <div className="space-y-5" data-testid="safe-assignments-panel">
      <div className="flex flex-wrap items-start justify-between gap-4 p-4 rounded-xl border border-[#E5E7EB] bg-white">
        <div className="text-sm text-[#374151] max-w-3xl">
          <div className="font-semibold text-[#0A2540] flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#059669]" /> Tier 1: safe assignments</div>
          <p className="mt-1">Only loans that have <b>no group yet</b>. Loans are grouped together only when the client name matches exactly (case/spacing/punctuation aside), the attorney email matches, the firm_id matches, and the client emails don't conflict. Every other loan gets its own group. <b>Review suggestions are never included here.</b></p>
        </div>
        <Button onClick={() => setOpen(true)} disabled={t.safe_loan_writes === 0} data-testid="apply-safe-btn"
                className="bg-[#059669] hover:bg-[#047857] text-white">
          Apply safe assignments ({t.safe_loan_writes} loans)
        </Button>
      </div>

      {grouped.length === 0 && <div className="text-sm text-[#6B7280]" data-testid="safe-empty">No multi-loan safe groups.</div>}
      {grouped.map((s) => <GroupCard key={s.proposal_id} s={s} />)}

      {plan.not_auto_grouped.length > 0 && (
        <div className="p-4 rounded-xl border border-[#FCD34D] bg-[#FFFBEB]" data-testid="not-auto-grouped">
          <div className="font-semibold text-sm text-[#92400E] mb-2">Exact-name loans held apart ({plan.not_auto_grouped.length})</div>
          {plan.not_auto_grouped.map((h) => (
            <div key={h.loan_id} className="text-xs text-[#78350F]"><span className="font-mono">{h.loan_id}</span> {h.client_name} — {h.reason}</div>
          ))}
        </div>
      )}

      <button onClick={() => setShowSingles(!showSingles)} className="flex items-center gap-1 text-sm text-[#0066FF]" data-testid="toggle-singles-btn">
        {showSingles ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        {singles.length} single-loan groups (no change in how they're grouped)
      </button>
      {showSingles && <LoanTable rows={singles.flatMap((s) => s.loans)} testid="singles-table" />}

      <ConfirmDialog open={open} onOpenChange={setOpen} testid="apply-safe-dialog" confirmLabel="Apply safe assignments"
        title="Apply Tier 1 safe assignments?"
        description="This writes a client_group_id to the loans listed below and nothing else. Review suggestions, firm_id values and loan balances are not touched. If the data changed since this dry run, the server will refuse and ask you to re-run it."
        onConfirm={apply}>
        <div className="grid grid-cols-3 gap-3 text-center text-sm">
          <div className="p-3 rounded-lg bg-[#F9FAFB]"><div className="text-2xl font-semibold tabular-nums">{t.safe_multi_loan_groups}</div>new multi-loan groups</div>
          <div className="p-3 rounded-lg bg-[#F9FAFB]"><div className="text-2xl font-semibold tabular-nums">{t.safe_joins_existing}</div>joins to existing groups</div>
          <div className="p-3 rounded-lg bg-[#F9FAFB]"><div className="text-2xl font-semibold tabular-nums">{t.safe_single_loan_groups}</div>single-loan groups</div>
        </div>
        <div className="text-xs text-[#6B7280]">Plan fingerprint: <span className="font-mono">{plan.plan_hash}</span> · {t.safe_loan_writes} loan records will get a client_group_id.</div>
      </ConfirmDialog>
    </div>
  );
};
